import { useState, useEffect } from 'react';
import { useMountedState } from 'react-use';

import { getSearchResults } from '@/lib/api/Search';
import { SearchResultsByCategory } from '@/lib/models/Search';
import { useDebounce } from '@/lib/hooks/useDebounce';

type UseSearchResultsReturn = {
  resultsByCategory: SearchResultsByCategory | null;
  loading: boolean;
  setQuery: (query: string) => void;
  query: string;
};

export function useSearchResults(locale: string): UseSearchResultsReturn {
  const [query, setQuery] = useState('');
  const [resultsByCategory, setResultsByCategory] = useState<SearchResultsByCategory | null>(null);
  const [loading, setLoading] = useState(false);

  const debouncedQuery = useDebounce(query, 300);
  const isMounted = useMountedState();

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResultsByCategory(null);
      return () => {};
    }

    const controller = new AbortController();
    const { signal } = controller;

    async function fetchResults() {
      setLoading(true);
      try {
        const searchResults = await getSearchResults(debouncedQuery, locale);
        if (!signal.aborted && isMounted()) {
          setResultsByCategory(searchResults);
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error('Search error:', error);
        }
      } finally {
        if (!signal.aborted && isMounted()) {
          setLoading(false);
        }
      }
    }

    fetchResults().catch(console.error);

    return () => {
      controller.abort();
    };
  }, [debouncedQuery, locale]);

  return { resultsByCategory, loading, setQuery, query };
}
